import { and, eq, getTableColumns, sql } from "drizzle-orm";

import type { Workspace } from "@storyboard/web-client";
import { updateWorkspaceConfig } from "@/data/workspaceConfig";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { workspace } from "@/db/schema";
import type { Workspace as WorkspaceType } from "@/db/types";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

export async function upsertWorkspace(workspaceRecord: Workspace) {
  updateWorkspaceConfig(workspaceRecord);
  await bulkUpsertWorkspace([workspaceRecord]);
}

export async function bulkUpsertWorkspace(records: Workspace[]) {
  if (records.length === 0) return;
  records.forEach(updateWorkspaceConfig);
  const data: WorkspaceType[] = records.map((workspaceRecord) => ({
    id: workspaceRecord.id,
    name: workspaceRecord.name,
    createdAt: workspaceRecord.createdAt ?? new Date().toISOString(),
    updatedAt: workspaceRecord.updatedAt,
  }));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(workspace, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [workspace.id],
      set: {
        name: sql`"excluded"."name"`,
        updatedAt: sql`"excluded"."updatedAt"`,
      },
    },
  });
  emitTableUpdates(["workspace"]);
}

export const getWorkspaces = () => {
  return db
    .select({
      ...getTableColumns(workspace),
    })
    .from(workspace);
};

export function findWorkspaceQuery({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      name: workspace.name,
    })
    .from(workspace)
    .where(and(eq(workspace.id, workspaceId)));

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Find Workspace", query);
  return query;
}
